import { useCallback, useEffect, useState } from "react";
import { adminContacts, adminContact, adminUpdateContact, api, API } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Download, Tag, XCircle, Trash2, BellOff } from "lucide-react";
import ResourceList from "@/components/admin/ResourceList";
import ResourceDrawer from "@/components/admin/ResourceDrawer";

const FIELDS = [
  "phone", "address", "zip", "parish", "diocese",
  "house_district", "senate_district", "county",
];

function ContactRow({ c, selected, onToggle, onOpen }) {
  return (
    <tr
      className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer"
      data-testid={`contact-row-${c.id}`}
    >
      <td className="px-3 py-3 w-8" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(c.id)}
          className="accent-ccc-purple"
          data-testid={`contact-select-${c.id}`}
          aria-label={`Select ${c.name}`}
        />
      </td>
      <td className="px-4 py-3 font-medium text-ccc-plum" onClick={() => onOpen(c.id)}>{c.name}</td>
      <td className="px-4 py-3 text-stone-700" onClick={() => onOpen(c.id)}>{c.email}</td>
      <td className="px-4 py-3 text-stone-600" onClick={() => onOpen(c.id)}>{c.zip || "—"}</td>
      <td className="px-4 py-3 text-stone-600" onClick={() => onOpen(c.id)}>{c.parish || "—"}</td>
      <td className="px-4 py-3 text-stone-600" onClick={() => onOpen(c.id)}>{c.contact_type}</td>
      <td className="px-4 py-3 text-right" onClick={() => onOpen(c.id)}>
        <span className="inline-block px-2 py-0.5 bg-ccc-purple text-white text-xs">{c.engagement_score}</span>
      </td>
    </tr>
  );
}

function ContactDetail({ detail, onSave }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
        {FIELDS.map(field => (
          <div key={field}>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
              {field.replace("_", " ")}
            </label>
            <input
              defaultValue={detail.contact[field] || ""}
              onBlur={e => e.target.value !== (detail.contact[field] || "") && onSave({ [field]: e.target.value })}
              className="w-full px-2 py-1 border border-stone-200 focus:outline-none focus:border-ccc-purple"
            />
          </div>
        ))}
      </div>
      <div className="text-xs uppercase tracking-wider text-ccc-gold-dark mb-2">Activity timeline</div>
      <div className="space-y-2">
        {detail.activities.length === 0 && <div className="text-sm text-stone-500">No activity yet.</div>}
        {detail.activities.map(a => (
          <div key={a.id} className="border-l-2 border-ccc-purple pl-3">
            <div className="text-xs uppercase tracking-wider text-stone-500">
              {a.type} &middot; {new Date(a.created_at).toLocaleDateString()}
            </div>
            <div className="text-sm text-ccc-charcoal">{a.detail}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function SavedViewsBar({ views, active, onApply, onSaveCurrent, onDelete }) {
  return (
    <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="saved-views-bar">
      <span className="text-xs uppercase tracking-wider text-stone-500 mr-1">Saved:</span>
      {views.length === 0 && <span className="text-xs text-stone-400 italic">No saved views yet.</span>}
      {views.map(v => (
        <div key={v.id} className={`inline-flex items-center border ${active === v.id ? "border-ccc-purple bg-ccc-purple/10" : "border-stone-300"}`}>
          <button onClick={() => onApply(v)} className="px-3 py-1 text-xs uppercase tracking-wider text-ccc-plum hover:bg-stone-50" data-testid={`saved-view-${v.name}`}>
            {v.name}
          </button>
          <button onClick={() => onDelete(v.id)} className="px-2 py-1 text-stone-400 hover:text-red-600" aria-label="Delete saved view">
            <XCircle size={12}/>
          </button>
        </div>
      ))}
      <button
        onClick={onSaveCurrent}
        data-testid="save-view-btn"
        className="ml-1 text-xs uppercase tracking-wider text-ccc-purple hover:text-ccc-purple-dark inline-flex items-center gap-1 border border-ccc-purple/40 px-2 py-1"
      >
        <Bookmark size={11}/> Save current
      </button>
    </div>
  );
}

function BulkBar({ count, onClear, onAction }) {
  const [tagValue, setTagValue] = useState("");
  const [typeValue, setTypeValue] = useState("");
  if (count === 0) return null;
  return (
    <div className="bg-ccc-plum text-ccc-ivory px-4 py-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
      <div className="text-sm font-medium">{count} selected</div>
      <div className="text-xs text-stone-300">·</div>

      <div className="inline-flex items-center gap-1">
        <input value={tagValue} onChange={e => setTagValue(e.target.value)} placeholder="tag name"
          className="w-32 px-2 py-1 text-xs bg-purple-950 border border-purple-800 focus:outline-none focus:border-ccc-gold"
          data-testid="bulk-tag-input"/>
        <button
          onClick={() => { if (tagValue.trim()) { onAction({ action: "add_tag", tag: tagValue.trim() }); setTagValue(""); } }}
          data-testid="bulk-add-tag"
          className="text-xs uppercase tracking-wider px-2 py-1 border border-purple-800 hover:border-ccc-gold inline-flex items-center gap-1">
          <Tag size={11}/> Tag
        </button>
      </div>

      <div className="inline-flex items-center gap-1">
        <select value={typeValue} onChange={e => setTypeValue(e.target.value)}
          className="text-xs bg-purple-950 border border-purple-800 py-1 px-2 focus:outline-none focus:border-ccc-gold">
          <option value="">Set type…</option>
          <option>Advocate</option><option>Donor</option><option>Parish</option>
          <option>Clergy</option><option>Legislator</option><option>Media</option><option>Partner</option>
        </select>
        <button
          onClick={() => { if (typeValue) { onAction({ action: "set_type", contact_type: typeValue }); setTypeValue(""); } }}
          data-testid="bulk-set-type"
          className="text-xs uppercase tracking-wider px-2 py-1 border border-purple-800 hover:border-ccc-gold">
          Apply
        </button>
      </div>

      <button
        onClick={() => { if (window.confirm(`Unsubscribe ${count} contacts?`)) onAction({ action: "unsubscribe" }); }}
        data-testid="bulk-unsubscribe"
        className="text-xs uppercase tracking-wider px-2 py-1 border border-purple-800 hover:border-red-500 inline-flex items-center gap-1">
        <BellOff size={11}/> Unsubscribe
      </button>

      <button
        onClick={() => { if (window.confirm(`Delete ${count} contacts? (Admin only, permanent)`)) onAction({ action: "delete" }); }}
        data-testid="bulk-delete"
        className="text-xs uppercase tracking-wider px-2 py-1 border border-purple-800 hover:border-red-500 inline-flex items-center gap-1">
        <Trash2 size={11}/> Delete
      </button>

      <button onClick={onClear} className="ml-auto text-xs text-stone-300 hover:text-white uppercase tracking-wider">
        Clear
      </button>
    </div>
  );
}

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [filters, setFilters] = useState({ tag: "", diocese: "", contact_type: "" });
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = { q };
    if (filters.tag) params.tag = filters.tag;
    if (filters.diocese) params.diocese = filters.diocese;
    return adminContacts(params).then(setItems);
  }, [q, filters.tag, filters.diocese]);

  const loadViews = useCallback(() => api.get("/admin/saved-views").then(r => setViews(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadViews(); }, [loadViews]);

  const open = async (id) => navigate(`/admin/contacts/${id}`);
  const save = async (patch) => {
    await adminUpdateContact(detail.contact.id, patch);
    toast.success("Contact updated");
    setDetail(await adminContact(detail.contact.id));
    load();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => (prev.size === items.length ? new Set() : new Set(items.map(i => i.id))));
  };

  const runBulk = async (payload) => {
    try {
      const r = await api.post("/admin/contacts/bulk", { contact_ids: [...selected], ...payload });
      toast.success(`Bulk ${payload.action}: ${r.data.modified ?? r.data.deleted ?? 0} contacts`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed");
    }
  };

  const saveCurrentView = async () => {
    const name = window.prompt("Name this view (e.g. 'Champions in HD-21')");
    if (!name?.trim()) return;
    try {
      await api.post("/admin/saved-views", { name: name.trim(), q, ...filters });
      toast.success("View saved");
      loadViews();
    } catch { toast.error("Save failed"); }
  };

  const applyView = (v) => {
    setActiveView(v.id);
    setQ(v.filters?.q || "");
    setFilters({ tag: v.filters?.tag || "", diocese: v.filters?.diocese || "", contact_type: v.filters?.contact_type || "" });
  };
  const deleteView = async (id) => {
    if (!window.confirm("Delete this view?")) return;
    await api.delete(`/admin/saved-views/${id}`);
    if (activeView === id) setActiveView(null);
    loadViews();
  };

  const exportUrl = `${API}/admin/export/contacts.csv?${new URLSearchParams({
    ...(q && { q }), ...(filters.tag && { tag: filters.tag }), ...(filters.diocese && { diocese: filters.diocese }),
  }).toString()}`;

  return (
    <ResourceList
      eyebrow="CRM"
      title="Contacts."
      testId="admin-contacts"
      search={{ value: q, onChange: setQ, placeholder: "Search name or email...", testId: "contacts-search" }}
      toolbar={
        <a href={exportUrl} data-testid="export-csv"
          className="text-xs uppercase tracking-wider px-3 py-2 border border-stone-300 hover:border-ccc-purple inline-flex items-center gap-1">
          <Download size={12}/> Export CSV
        </a>
      }
    >
      <SavedViewsBar
        views={views}
        active={activeView}
        onApply={applyView}
        onSaveCurrent={saveCurrentView}
        onDelete={deleteView}
      />
      <BulkBar count={selected.size} onClear={() => setSelected(new Set())} onAction={runBulk}/>

      <div className="bg-white border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ccc-plum text-ccc-ivory">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={toggleAll}
                  className="accent-ccc-gold"
                  data-testid="select-all"
                  aria-label="Select all"
                />
              </th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">ZIP</th>
              <th className="text-left px-4 py-3">Parish</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Score</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <ContactRow key={c.id} c={c} selected={selected.has(c.id)} onToggle={toggleSelect} onOpen={open}/>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-stone-500 py-12">
                No contacts yet. Public signups appear here immediately.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ResourceDrawer
        open={!!detail}
        onClose={() => setDetail(null)}
        testId="contact-drawer"
        subtitle={detail?.contact.contact_type}
        title={detail?.contact.name}
        footer={detail?.contact.email}
      >
        {detail && <ContactDetail detail={detail} onSave={save}/>}
      </ResourceDrawer>
    </ResourceList>
  );
}
